import { useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { useGetFeedStatTypesQuery, useGetFeedTeamsQuery } from '@/api/feed';
import { QuestionType, FixtureSelection } from '../QuestionCoachModal';
import { MARGIN_CONSTRAINTS } from '@/constants/question-coach';

interface StatTypeStepProps {
  sportId?: string;
  leagueId?: string;
  questionType?: QuestionType;
  selectedStatTypeCode?: string;
  margin?: number;
  onSelect: (statTypeCode: string, statTypeName: string) => void;
  onMarginChange?: (margin: number | undefined) => void;
  /**
   * Batch creation mode (DKPLAT-501, team_over_under and player_over_under): stat
   * type stays a single shared selection for the whole batch; render one threshold
   * card per selected fixture, with one input per selected team/player in it.
   */
  fixtures?: FixtureSelection[];
  onThresholdChange?: (fixtureId: string, entityId: string, margin: number | undefined) => void;
}

export function StatTypeStep({
  sportId,
  leagueId,
  questionType,
  selectedStatTypeCode,
  margin,
  onSelect,
  onMarginChange,
  fixtures,
  onThresholdChange,
}: StatTypeStepProps) {
  const { data: statTypes, isLoading, error } = useGetFeedStatTypesQuery(sportId);
  const { data: teams } = useGetFeedTeamsQuery(fixtures ? leagueId : undefined);

  const teamMap = useMemo(() => {
    const map = new Map<string, string>();
    teams?.forEach(team => map.set(team.externalId, team.name));
    return map;
  }, [teams]);

  const isOverUnder = questionType === 'player_over_under' || questionType === 'team_over_under';

  // Validate margin for over/under questions
  const isMarginValid = useMemo(() => {
    if (!isOverUnder) return true; // Not required for selection questions
    if (margin === undefined || margin === null) return false;
    return margin >= MARGIN_CONSTRAINTS.MIN;
  }, [isOverUnder, margin]);

  // Filter stat types based on question type
  const filteredStatTypes = statTypes?.filter(statType => {
    if (questionType === 'player_selection' || questionType === 'player_over_under') {
      return statType.category === 'player';
    }
    return statType.category === 'team';
  });

  if (!sportId) {
    return (
      <Alert>
        <AlertDescription>Please select a sport first.</AlertDescription>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading stat types...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load stat types. Please try again.</AlertDescription>
      </Alert>
    );
  }

  if (!filteredStatTypes || filteredStatTypes.length === 0) {
    return (
      <Alert>
        <AlertDescription>
          No stat types available for this sport and question type.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="stat-type-select">Stat Type</Label>
        <Select
          value={selectedStatTypeCode || ''}
          onValueChange={value => {
            const statType = filteredStatTypes.find(st => st.code === value);
            if (statType) {
              onSelect(statType.code, statType.name);
            }
          }}
        >
          <SelectTrigger id="stat-type-select">
            <SelectValue placeholder="Select a stat type" />
          </SelectTrigger>
          <SelectContent>
            {filteredStatTypes.map(statType => (
              <SelectItem key={statType.code} value={statType.code}>
                {statType.name}
                {statType.unit && ` (${statType.unit})`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-sm text-muted-foreground">
          Choose the statistic that this question will be based on
        </p>
      </div>

      {/* Batch creation mode (DKPLAT-501): one card per fixture, one threshold input
          per selected team (team_over_under) or player (player_over_under). */}
      {isOverUnder && selectedStatTypeCode && fixtures && (
        <div className="space-y-4">
          {fixtures.map(fixture => {
            const isPlayerMode = questionType === 'player_over_under';
            const entityIds = isPlayerMode ? (fixture.playerIds ?? []) : fixture.selectedTeamIds;
            const entityName = (entityId: string, index: number) =>
              isPlayerMode
                ? fixture.playerNames?.[index] || entityId
                : teamMap.get(entityId) || entityId;

            return (
              <Card key={fixture.fixtureId} className="p-4 space-y-3">
                <Label className="text-sm font-semibold">{fixture.fixtureDisplay}</Label>
                {entityIds.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No {isPlayerMode ? 'players' : 'teams'} selected for this fixture.
                  </p>
                ) : (
                  entityIds.map((entityId, index) => {
                    const name = entityName(entityId, index);
                    const entityMargin = fixture.thresholds[entityId];
                    const entityMarginValid =
                      entityMargin !== undefined && entityMargin >= MARGIN_CONSTRAINTS.MIN;
                    return (
                      <div key={entityId} className="space-y-1">
                        <Label htmlFor={`margin-${fixture.fixtureId}-${entityId}`}>{name}</Label>
                        <Input
                          id={`margin-${fixture.fixtureId}-${entityId}`}
                          type="number"
                          step={MARGIN_CONSTRAINTS.STEP}
                          min={MARGIN_CONSTRAINTS.MIN}
                          placeholder={MARGIN_CONSTRAINTS.PLACEHOLDER}
                          value={entityMargin !== undefined ? entityMargin : ''}
                          onChange={e => {
                            const value = e.target.value;
                            if (!value) {
                              onThresholdChange?.(fixture.fixtureId, entityId, undefined);
                              return;
                            }
                            const parsed = parseFloat(value);
                            onThresholdChange?.(
                              fixture.fixtureId,
                              entityId,
                              isNaN(parsed) ? undefined : parsed
                            );
                          }}
                          className={
                            !entityMarginValid && entityMargin !== undefined
                              ? 'border-destructive'
                              : ''
                          }
                        />
                      </div>
                    );
                  })
                )}
              </Card>
            );
          })}
        </div>
      )}

      {isOverUnder && selectedStatTypeCode && !fixtures && (
        <div className="space-y-2">
          <Label htmlFor="margin-input">Threshold / Margin</Label>
          <Input
            id="margin-input"
            type="number"
            step={MARGIN_CONSTRAINTS.STEP}
            min={MARGIN_CONSTRAINTS.MIN}
            required={isOverUnder}
            placeholder={MARGIN_CONSTRAINTS.PLACEHOLDER}
            value={margin !== undefined ? margin : ''}
            onChange={e => {
              const value = e.target.value;
              if (!value) {
                onMarginChange?.(undefined);
                return;
              }
              const parsed = parseFloat(value);
              onMarginChange?.(isNaN(parsed) ? undefined : parsed);
            }}
            className={!isMarginValid && margin !== undefined ? 'border-destructive' : ''}
          />
          <p className="text-sm text-muted-foreground">
            Enter the over/under threshold for this question (e.g., 300 yards, 27.5 points)
          </p>
        </div>
      )}

      {selectedStatTypeCode && !fixtures && (
        <Alert>
          <AlertDescription>
            <strong>Selected:</strong>{' '}
            {filteredStatTypes.find(st => st.code === selectedStatTypeCode)?.name}
            {isOverUnder && margin !== undefined && ` - Threshold: ${margin}`}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
